import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { CheckboxGroupField, FilterField, FilterResult, GroupChildField, InputField, RadioGroupField, RadioGroupOption } from './model/filter-field.model';

/** Internal key pattern for checkbox boolean controls */
const CHK_PREFIX = '__chk__';
const chkKey = (groupName: string, optVal: string) =>
  `${groupName}${CHK_PREFIX}${optVal}`;

@Component({
  selector: 'app-filter-panel',
  templateUrl: './filter-panel.component.html',
  styleUrls: ['./filter-panel.component.css'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
})

export class FilterPanelComponent implements OnInit, OnChanges, OnDestroy {
  // ── Inputs ──────────────────────────────────────────────────────────────────

  /** Field config — can be mutated at runtime; the form rebuilds automatically */
  @Input() fields: FilterField[] = [];

  /** Dropdown header title */
  @Input() title = 'Advanced Filter';

  /** Tooltip on the funnel button */
  @Input() buttonTitle = 'Filter';

  // ── Outputs ─────────────────────────────────────────────────────────────────

  @Output() filterApplied = new EventEmitter<FilterResult>();
  @Output() filterCleared = new EventEmitter<void>();

  // ── State ───────────────────────────────────────────────────────────────────
  isOpen = false;
  isFilterApplied = false;
  form!: FormGroup;

  /** Active radio value per radio-group field. Key = field.controlName */
  activeRadioValues: Record<string, string> = {};

  /** Checked state per checkbox option. Key = chkKey(groupName, optVal) */
  checkedState: Record<string, boolean> = {};

  private destroy$ = new Subject<void>();

  constructor(private fb: FormBuilder, private elRef: ElementRef) { }

  // ── Lifecycle ────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.buildForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fields'] && !changes['fields'].firstChange) {
      this.destroy$.next();
      this.buildForm();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Form construction ────────────────────────────────────────────────────────

  private buildForm(): void {
    const controls: Record<string, any> = {};
    this.checkedState = {};
    this.activeRadioValues = {};

    for (const field of this.fields) {
      if (this.isRadioGroup(field)) {
        // Auto-collapse: single option → treat as plain input
        if (field.options.length === 1) {
          this.addChildControl(controls, field.options[0].field, true);
        } else {
          this.initRadioGroupControls(field, controls);
        }
      } else if (this.isCheckboxGroup(field)) {
        // Auto-collapse: single option → treat as plain input
        if (field.options.length === 1) {
          this.addChildControl(controls, field.options[0].field, true);
        } else {
          this.initCheckboxGroupControls(field, controls);
        }
      } else {
        const f = field as InputField;
        controls[f.controlName] = [f.defaultValue ?? '', f.validators ?? []];
      }
    }

    this.form = this.fb.group(controls);
    this.subscribeToRadioChanges();
    this.subscribeToCheckboxChanges();
  }

  // ── Radio group init ──────────────────────────────────────────────────────

  private initRadioGroupControls(
    field: RadioGroupField,
    controls: Record<string, any>
  ): void {
    const defaultVal = field.defaultValue ?? field.options[0]?.value ?? '';
    controls[field.controlName] = [defaultVal];
    this.activeRadioValues[field.controlName] = defaultVal;

    for (const opt of field.options) {
      const isDefault = opt.value === defaultVal;
      this.addChildControl(controls, opt.field, isDefault);
    }
  }

  private subscribeToRadioChanges(): void {
    for (const field of this.fields) {
      if (!this.isRadioGroup(field) || field.options.length === 1) continue;

      this.form
        .get(field.controlName)
        ?.valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe((selected: string) => {
          this.activeRadioValues[field.controlName] = selected;
          this.swapRadioValidators(field, selected);
        });
    }
  }

  private swapRadioValidators(field: RadioGroupField, activeValue: string): void {
    for (const opt of field.options) {
      const ctrl = this.form.get(opt.field.controlName);
      if (!ctrl) continue;
      if (opt.value !== activeValue) {
        ctrl.reset('');
        ctrl.clearValidators();
        ctrl.markAsUntouched();
      } else {
        ctrl.setValidators(opt.field.validators ?? []);
        // If the user already tried to submit or touched a field,
        // mark the newly active control touched immediately so its
        // validation error shows right away on radio switch.
        if (this.form.touched) {
          ctrl.markAsTouched();
        }
      }
      ctrl.updateValueAndValidity({ emitEvent: false });
    }
  }

  // ── Checkbox group init ───────────────────────────────────────────────────

  private initCheckboxGroupControls(
    field: CheckboxGroupField,
    controls: Record<string, any>
  ): void {
    const defaultChecked = new Set(field.defaultChecked ?? []);

    for (const opt of field.options) {
      const key = chkKey(field.controlName, opt.value);
      const isChecked = defaultChecked.has(opt.value);
      controls[key] = [isChecked];
      this.checkedState[key] = isChecked;
      // Active when pre-checked
      this.addChildControl(controls, opt.field, isChecked);
    }
  }

  private subscribeToCheckboxChanges(): void {
    for (const field of this.fields) {
      if (!this.isCheckboxGroup(field) || field.options.length === 1) continue;

      for (const opt of field.options) {
        const key = chkKey(field.controlName, opt.value);
        this.form
          .get(key)
          ?.valueChanges.pipe(takeUntil(this.destroy$))
          .subscribe((checked: boolean) => {
            this.checkedState[key] = checked;
            this.swapCheckboxValidators(opt.field, checked);
          });
      }
    }
  }

  private swapCheckboxValidators(childField: GroupChildField, active: boolean): void {
    const ctrl = this.form.get(childField.controlName);
    if (!ctrl) return;
    if (!active) {
      ctrl.reset(childField.defaultValue ?? '');
      ctrl.clearValidators();
    } else {
      ctrl.setValidators(childField.validators ?? []);
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  private addChildControl(
    controls: Record<string, any>,
    field: GroupChildField,
    withValidators: boolean
  ): void {
    controls[field.controlName] = [
      field.defaultValue ?? '',
      withValidators ? (field.validators ?? []) : [],
    ];
  }

  // ── Type guards ───────────────────────────────────────────────────────────

  isRadioGroup(field: FilterField): field is RadioGroupField {
    return field.type === 'radio-group';
  }

  isCheckboxGroup(field: FilterField): field is CheckboxGroupField {
    return field.type === 'checkbox-group';
  }

  isStandalone(field: FilterField): field is InputField {
    return field.type !== 'radio-group' && field.type !== 'checkbox-group';
  }

  // ── Collapse helpers (used in template) ───────────────────────────────────

  /** True when a radio/checkbox group should render as a plain input */
  isCollapsed(field: RadioGroupField | CheckboxGroupField): boolean {
    return field.options.length === 1;
  }

  collapsedChild(
    field: RadioGroupField | CheckboxGroupField
  ): GroupChildField {
    return field.options[0].field;
  }

  collapsedLabel(field: RadioGroupField | CheckboxGroupField): string {
    return field.label;
  }

  // ── Radio helpers ─────────────────────────────────────────────────────────

  getActiveRadioOption(field: RadioGroupField): RadioGroupOption | undefined {
    return field.options.find(
      (o) => o.value === this.activeRadioValues[field.controlName]
    );
  }

  /**
   * Used by the template to show only the active radio option's child input.
   * Using *ngFor + *ngIf(isCheckedRadio) forces Angular to DESTROY and
   * RECREATE the input element on each switch — avoiding the ControlValueAccessor
   * desync that happens when ngTemplateOutlet reuses the same DOM node and just
   * changes [formControlName] to point at a different FormControl.
   */
  isCheckedRadio(groupControlName: string, optValue: string): boolean {
    return this.activeRadioValues[groupControlName] === optValue;
  }

  // ── Checkbox helpers ──────────────────────────────────────────────────────

  isChecked(groupControlName: string, optValue: string): boolean {
    return !!this.checkedState[chkKey(groupControlName, optValue)];
  }

  chkControlName(groupControlName: string, optValue: string): string {
    return chkKey(groupControlName, optValue);
  }

  // ── Validation helpers ────────────────────────────────────────────────────

  isFieldInvalid(controlName: string): boolean {
    const ctrl = this.form.get(controlName);
    return !!(ctrl?.invalid && ctrl.touched);
  }

  getError(controlName: string): string | null {
    const ctrl = this.form.get(controlName);
    if (!ctrl?.touched || !ctrl.invalid) return null;
    const e = ctrl.errors;
    if (!e) return null;
    if (e['required']) return 'This field is required';
    if (e['pattern']) return 'Invalid format';
    if (e['minlength']) return `Minimum ${e['minlength'].requiredLength} characters`;
    if (e['maxlength']) return `Maximum ${e['maxlength'].requiredLength} characters`;
    if (e['min']) return `Value must be at least ${e['min'].min}`;
    if (e['max']) return `Value must be at most ${e['max'].max}`;
    if (e['email']) return 'Enter a valid email address';
    return 'Invalid value';
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  toggle(): void {
    this.isOpen = !this.isOpen;
  }

  close(): void {
    this.isOpen = false;
  }

  apply(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    const result: FilterResult = {};

    for (const field of this.fields) {

      // ── Standalone ──────────────────────────────────────────────────────
      if (this.isStandalone(field)) {
        const val = this.trim(this.form.get(field.controlName)?.value);
        if (this.hasValue(val)) result[field.controlName] = val;

        // ── Radio group ─────────────────────────────────────────────────────
      } else if (this.isRadioGroup(field)) {
        if (this.isCollapsed(field)) {
          // Single option collapsed to plain input
          const child = this.collapsedChild(field);
          const val = this.trim(this.form.get(child.controlName)?.value);
          if (this.hasValue(val)) result[child.controlName] = val;
        } else {
          const active = this.getActiveRadioOption(field);
          if (!active) continue;
          // Emit the radio selection itself (e.g. filterType: 'CID')
          result[field.controlName] = active.value;
          // Emit the child input value (e.g. filterValue: '4340725617')
          const val = this.trim(this.form.get(active.field.controlName)?.value);
          if (this.hasValue(val)) result[active.field.controlName] = val;
        }

        // ── Checkbox group ───────────────────────────────────────────────────
      } else if (this.isCheckboxGroup(field)) {
        if (this.isCollapsed(field)) {
          const child = this.collapsedChild(field);
          const val = this.trim(this.form.get(child.controlName)?.value);
          if (this.hasValue(val)) result[child.controlName] = val;
        } else {
          for (const opt of field.options) {
            if (!this.isChecked(field.controlName, opt.value)) continue;
            const val = this.trim(this.form.get(opt.field.controlName)?.value);
            if (this.hasValue(val)) result[opt.field.controlName] = val;
          }
        }
      }
    }

    this.isFilterApplied = Object.keys(result).length > 0;
    this.filterApplied.emit(result);
    this.close();
  }

  /**
   * Public reset — call this from a parent component to programmatically
   * clear the filter without triggering the filterCleared output loop.
   * Works whether the dropdown is open or closed.
   */
  reset(): void {
    // console.log('FilterPanelComponent: reset() called');
    this._resetForm();
    this.isFilterApplied = false;
    this.isOpen = false;
  }

  clear(): void {
    this._resetForm();
    this.isFilterApplied = false;
    this.filterCleared.emit();
    this.close();
  }

  private _resetForm(): void {
    const resetValues: Record<string, any> = {};

    for (const field of this.fields) {
      if (this.isRadioGroup(field)) {
        if (this.isCollapsed(field)) {
          const child = this.collapsedChild(field);
          resetValues[child.controlName] = child.defaultValue ?? '';
        } else {
          const defaultVal = field.defaultValue ?? field.options[0]?.value ?? '';
          resetValues[field.controlName] = defaultVal;
          this.activeRadioValues[field.controlName] = defaultVal;
          for (const opt of field.options) {
            resetValues[opt.field.controlName] = opt.field.defaultValue ?? '';
          }
          this.swapRadioValidators(field, defaultVal);
        }
      } else if (this.isCheckboxGroup(field)) {
        if (this.isCollapsed(field)) {
          const child = this.collapsedChild(field);
          resetValues[child.controlName] = child.defaultValue ?? '';
        } else {
          const defaultChecked = new Set(field.defaultChecked ?? []);
          for (const opt of field.options) {
            const key = chkKey(field.controlName, opt.value);
            const isChecked = defaultChecked.has(opt.value);
            resetValues[key] = isChecked;
            this.checkedState[key] = isChecked;
            resetValues[opt.field.controlName] = opt.field.defaultValue ?? '';
            this.swapCheckboxValidators(opt.field, isChecked);
          }
        }
      } else {
        const f = field as InputField;
        resetValues[f.controlName] = f.defaultValue ?? '';
      }
    }

    this.form.reset(resetValues);
  }

  // ── Utilities ─────────────────────────────────────────────────────────────

  private trim(val: any): any {
    return typeof val === 'string' ? val.trim() : val;
  }

  private hasValue(val: any): boolean {
    return val !== '' && val !== null && val !== undefined;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isOpen && !this.elRef.nativeElement.contains(event.target)) {
      this.close();
    }
  }
}